
import React, { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import AmpButton from "../../components/common/AmpButton/AmpButton";
import AmpFormError from "../../components/common/AmpFormError/AmpFormError";
import AmpInput from "../../components/common/AmpInput/AmpInput";
import AmpLoadingSpinner from "../../components/common/AmpLoadingSpinner/AmpLoadingSpinner";
import SmallHeading from "../../components/common/AmpSmallHeading/AppSmallHeading";
import SuccessOverlay from "../../components/common/SuccessOverlay/SuccessOverlay";
import PasswordValidator from "../../components/login/PasswordValidator/PasswordValidator";
import { resendEmail } from "../../redux/actions/auth/reg";
import { submitReset, submitReset2 } from "../../redux/actions/auth/reset";
import { setToken } from "../../redux/actions/auth/token";
import { setLargeToast, setToastNotification } from "../../redux/actions/toastNotification/toastNotification";
import { SUBMIT_PWD_RESET, VERIFY_EMAIL_RESET } from "../../redux/actionTypes/actionsTypes";
import { ResendEmailResponse } from "../../redux/reducers/getAuthReducer/resendVerifyEmailReducer";
import { ResetPassword } from "../../redux/reducers/getAuthReducer/resetPasswordReducer";
import { RootState } from "../../redux/reducers/root";
import extractFormData from "../../utils/extractFormData";

import "./ResetFinalStep.css";

interface ResetFinalStepProps {
    noCode?: boolean
}

export default function ResetFinalStep(props: ResetFinalStepProps): JSX.Element {
    const { user_profile_id, username } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { t } = useTranslation("login");
    const resetStore = useSelector((state: RootState) => state.authReducer.reset.finish as ResetPassword);
    const resendResult = useSelector((state: RootState) => state.authReducer.resend as ResendEmailResponse);
    
    const { token } = useParams();

    const [success, setSuccess] = useState(false);
    const [resendEnabled, setResendEnabled] = useState(true);
    const [resendTimeRemaining, setResendTimeRemaining] = useState(0);
    const [pwd, setPwd] = useState("");
    const [confPwd, setConfPwd] = useState("");
    const [code, setCode] = useState("");
    const [pwdMatchError, setPasswordMatchError] = useState(false);
    const [isWrongCode, setWrongCode] = useState(false);

    useEffect(() => {
        if (pwdMatchError) {
            setPasswordMatchError(false);
        }
        if (isWrongCode) {
            setWrongCode(false);
        }
    // eslint-disable-next-line
    }, [confPwd, code])


    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        dispatch({ type: SUBMIT_PWD_RESET.RESET });
        const data = extractFormData(e);
        const payload = {
            code: data["code"],
            password: data["new_password"],
            conf_password: data["conf_password"]
        }

        if ((payload.password !== payload.conf_password) || (pwd === "" || confPwd === "")){
            setPasswordMatchError(true);
        }else if (props.noCode) {
            delete payload.code;
            dispatch(submitReset2(payload, token as string))
        } else {
            dispatch(submitReset(payload, token as string))
        }
    }

    useEffect(() => {
        if (resetStore.resetted && !resetStore.loading) {
            setSuccess(true);
            dispatch(setToken(false));
            dispatch({type: VERIFY_EMAIL_RESET})
            setTimeout(() => {
                navigate("/auth/home/reg-user");
                dispatch(setLargeToast(t("pwd-changed"), "success", true));
            }, 5000);
        } else if (resetStore.status !== 200 && !resetStore.loading) {
            setWrongCode(true);
        }
    }, [resetStore, dispatch, navigate, t])

    useEffect( () => {
        if (!resendResult["loading"] && resendResult["sent"]) {
            let timeRemaining = resendTimeRemaining;
            const interval = setInterval(() => {
                if (timeRemaining !== 0){
                    setResendTimeRemaining(timeRemaining - 1);
                } else {
                    setResendEnabled(true);
                    clearInterval(interval);
                }
                timeRemaining--;
            }, 1000);
        }
    }, [resendResult, resendTimeRemaining, dispatch])

    const resend = (e: FormEvent) => {
        e.preventDefault();
        setResendEnabled(false);
        dispatch(resendEmail(user_profile_id as string,username as string, true));
        dispatch(setToastNotification("Email Sent", "success"));
        setResendTimeRemaining(15);
    }

    return (
        <div className="reset-top">
            <div className="reset-container pwd">
                <div className="reset-header">
                    {t("5-forgot-heading") as string}
                </div>
                <SuccessOverlay success={success}>
                    <AmpLoadingSpinner loading={resetStore.loading}>
                        <form className="reset-content-top" onSubmit={(e) => { handleSubmit(e) }}>
                            <div className="reset-content">
                                {!props.noCode && (
                                    <>
                                        <SmallHeading for={"verify-code"}>
                                            {t("code") as string}
                                        </SmallHeading>

                                        <AmpInput type="text" name="code" id="verify-code" autoComplete="one-time-code" noTimer={true} onInput={(e) => setCode(e)} />
                                    </>
                                )
                                }
                                <div className="side">
                                    <div>
                                        <SmallHeading for={"password"}>
                                            {t("2-pwd") as string}
                                        </SmallHeading>
                                        <AmpInput type="password" name="new_password" id="new-password" autoComplete="new-password" noTimer={true} onInput={(e) => setPwd(e)} />

                                        <SmallHeading for={"conf-password"}>
                                            {t("2-conf-pwd") as string}
                                        </SmallHeading>
                                        <AmpInput type="password" name="conf_password" id="conf-password" autoComplete="new-password" noTimer={true} onInput={(e) => setConfPwd(e)} />
                                        <AmpFormError errorMessage={t("2-conf-pwd-not-match")} error={pwdMatchError}></AmpFormError>
                                        <AmpFormError errorMessage={t("5-wrong-code")} error={isWrongCode}></AmpFormError>
                                    </div>
                                    <div>
                                        <PasswordValidator password={pwd} />
                                    </div>
                                </div>
                                
                            </div>

                            <div className="login-container-footer">
                                <Link rel="noreferrer noopener" target="_blank" className="amp-anchor" to="contact-us">{t("need-help") as string}</Link>
                                <div>
                                    <span>{resendTimeRemaining !== 0 ? resendTimeRemaining : <></>}</span>
                                    <AmpButton type="submit" onClick={(e) => resend(e)} text={t("resend")} disabled={!resendEnabled} />
                                    <AmpButton type="submit" onClick={() => null} text={t("submit")} />
                                </div>
                            </div>
                        </form>
                    </AmpLoadingSpinner>
                </SuccessOverlay>
            </div>
        </div>
    )
}